import * as React from "react";
import DayPicker, {
  DateUtils,
  DayPickerProps,
  RangeModifier,
} from "react-day-picker";
import { createGlobalStyle } from "styled-components";

import {
  DateRangePickerHeader,
  DateRangePickerHeaderProps,
} from "./DateRangePickerHeader";

import dayjs from "dayjs";
import { SvgSprite } from "../SvgSprite";

type FilteredDayPickerProps = Omit<
  DayPickerProps,
  "onDayClick" | "modifiers" | "selectedDays"
>;

interface Preset {
  range: RangeModifier;
  label: React.ReactNode;
}

export interface DateRangePickerProps extends FilteredDayPickerProps {
  id: string;

  onChange?: (range: RangeModifier) => void;

  from?: Date;
  to?: Date;

  innerRef?: React.MutableRefObject<{
    header: DateRangePickerHeaderProps["innerRef"]["current"];
  }>;

  presets: Preset[];
  onPresetClick: (
    preset: Preset,
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => void;
}

export const DateRangePicker: React.FC<DateRangePickerProps> = ({
  id,
  onChange,
  onMonthChange,
  from,
  to,
  innerRef,
  presets,
  onPresetClick,
  ...props
}) => {
  const [dayPickerStyles, setDayPickerStyles] = React.useState(undefined);

  React.useEffect(() => {
    setDayPickerStyles(import("react-day-picker/lib/style.css"));
  }, []);

  const GlobalStyles = createGlobalStyle`
    ${dayPickerStyles}
  `;

  const [calendarState, setCalendarState] = React.useState(() => {
    return {
      date: from || new Date(),
    };
  });

  const modifiers = {
    start: from,
    end: to,
  };

  const headerRef =
    React.useRef<DateRangePickerHeaderProps["innerRef"]["current"]>(null);

  React.useImperativeHandle(innerRef, () => ({
    header: headerRef.current,
  }));

  return (
    <>
      <GlobalStyles />
      <DateRangePickerHeader
        innerRef={headerRef}
        id={id}
        month={calendarState.date.getMonth()}
        year={calendarState.date.getFullYear()}
        onMonthChange={(month) => {
          const newDate = dayjs(calendarState.date).set("M", month).toDate();
          setCalendarState((prev) => ({
            ...prev,
            date: newDate,
          }));
        }}
        onYearChange={(year) => {
          const newDate = dayjs(calendarState.date).set("y", year).toDate();
          setCalendarState((prev) => ({
            ...prev,
            date: newDate,
          }));
        }}
      />
      <nav
        className="w-100 btn-group mb-1"
        css={`
          .btn-group:not(:first-child),
          .btn:not(:first-child) {
            margin-left: -1px;
          }
        `}
      >
        <button
          type="button"
          className="btn p-2 px-3 btn-outline-dark border btn-sm flex-grow-0 text-left"
          onClick={(e) => {
            const newDate = dayjs(calendarState.date).subtract(1, "M").toDate();
            setCalendarState((prev) => ({
              ...prev,
              date: newDate,
            }));
          }}
          aria-label="Previous month"
        >
          <SvgSprite
            id="icons:chev-left"
            width={14}
            height={14}
            aria-label="Previous"
          />
        </button>
        <button
          type="button"
          className="btn p-2 btn-outline-dark border btn-sm flex-grow-1"
          onClick={(e) => {
            setCalendarState((prev) => ({
              ...prev,
              date: new Date(),
            }));
          }}
        >
          Today
        </button>
        <button
          type="button"
          className="btn p-2 px-3 btn-outline-dark border btn-sm flex-grow-0 text-right"
          onClick={(e) => {
            const newDate = dayjs(calendarState.date).add(1, "M").toDate();
            setCalendarState((prev) => ({
              ...prev,
              date: newDate,
            }));
          }}
          aria-label="Next month"
        >
          <SvgSprite
            id="icons:chev-right"
            width={14}
            height={14}
            aria-label="Next"
          />
        </button>
      </nav>
      <DayPicker
        css={`
          .DayPicker-wrapper {
            padding-bottom: 0;
          }

          .DayPicker-Month {
            margin: 0;
          }

          .DayPicker-Day--selected:not(.DayPicker-Day--disabled):not(.DayPicker-Day--outside) {
            background-color: ${(p) => p.theme.linkColor};
            color: #f0f8ff;
          }

          .DayPicker-Day {
            border-radius: 0 !important;
            width: 2rem;
            height: 2rem;
          }

          .DayPicker-Day--today {
            color: initial;
            font-weight: normal;
          }

          .DayPicker-Day--today:not(.DayPicker-Day--selected) {
            position: relative;

            &:after {
              content: "";
              left: 0.25rem;
              right: 0.25rem;
              bottom: 0.25rem;
              position: absolute;
              border-bottom: 2px solid ${(p) => p.theme.primaryColor};
            }
          }
        `}
        numberOfMonths={1}
        month={calendarState.date}
        onMonthChange={(month) => {
          setCalendarState((prev) => ({
            ...prev,
            date: month,
          }));

          if (onMonthChange) {
            onMonthChange(month);
          }
        }}
        selectedDays={[
          from,
          {
            from,
            to,
          },
        ]}
        modifiers={modifiers}
        onDayClick={(...args): void => {
          const range = DateUtils.addDayToRange(args[0], {
            from: from,
            to: to,
          });

          if (range.to == null) {
            range.to = range.from;
          }

          if (onChange) {
            onChange(range);
          }
        }}
        captionElement={() => {
          return null;
        }}
        navbarElement={(props) => {
          return null;
        }}
        {...props}
      />

      <div className="d-flex justify-content-between flex-wrap align-items-center mt-3 mb-2">
        {presets.map((preset, idx) => (
          <button
            key={idx}
            type="button"
            className="btn btn-light btn-sm py-1 rounded-pill mb-2 mx-auto"
            onClick={(e) => {
              onPresetClick(preset, e);
            }}
          >
            {preset.label}
          </button>
        ))}
      </div>
    </>
  );
};
